import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function SalesFooter() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = useCallback(() => setActiveModal(null), []);
  useEffect(() => {
    if (!activeModal) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [activeModal, closeModal]);
  return <>
      <footer className="py-16 bg-black border-t border-neutral-900" data-id="element-662">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-663">
          <p className="font-serif text-xl md:text-2xl text-white font-semibold mb-1" data-id="element-664">
            Who We Are, Who They Are, and What They Hide
          </p>
          <p className="font-serif text-lg text-teal-400 italic mb-8" data-id="element-665">
            How to Play the Real Game
          </p>

          <nav className="flex items-center justify-center gap-6 mb-8" data-id="element-666">
            <a href="#the-pack" className="text-neutral-500 text-sm hover:text-teal-400 transition-colors" data-id="element-667">
              The Book
            </a>
            <a href="#authors" className="text-neutral-500 text-sm hover:text-teal-400 transition-colors" data-id="element-668">
              The Authors
            </a>
          </nav>

          <div className="border-t border-neutral-800/50 pt-8" data-id="element-669">
            <p className="text-neutral-600 text-sm mb-3" data-id="element-670">
              © 2025 Nicolás Pereira & Gabriel Beltrán. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-3 text-neutral-700 text-xs" data-id="element-671">
              <button onClick={() => setActiveModal('privacy')} className="hover:text-neutral-400 transition-colors cursor-pointer" data-id="element-672">
                Privacy Policy
              </button>
              <span data-id="element-673">•</span>
              <button onClick={() => setActiveModal('terms')} className="hover:text-neutral-400 transition-colors cursor-pointer" data-id="element-674">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-675">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeModal} data-id="element-676">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="relative w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-677">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-800/70" data-id="element-678">
                <h2 className="font-serif text-lg md:text-xl text-teal-400 font-semibold" data-id="element-679">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={closeModal} aria-label="Close" className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors" data-id="element-680">
                  <XIcon className="w-5 h-5" data-id="element-681" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[70vh] text-neutral-400 text-sm leading-relaxed space-y-6" data-id="element-682">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-683" /> : <TermsContent data-id="element-684" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-neutral-500 text-xs" data-id="element-685">Last updated: February 2026</p>

      <div data-id="element-686">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-687">
          1. Information We Collect
        </h3>
        <p data-id="element-688">
          When you purchase our digital products (ebook and/or audiobook), we
          collect only the information necessary to process your transaction and
          deliver your purchase. This may include your name, email address, and
          payment information. Payment processing is handled by secure
          third-party providers — we do not store your credit card details.
        </p>
      </div>

      <div data-id="element-689">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-690">
          2. How We Use Your Information
        </h3>
        <p data-id="element-691">
          Your information is used exclusively to: deliver your purchased
          digital products, send purchase confirmations and download links,
          provide customer support related to your order, and send occasional
          updates about new releases (only if you opt in). We will never sell,
          rent, or share your personal information with third parties for
          marketing purposes.
        </p>
      </div>

      <div data-id="element-692">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-693">
          3. Cookies & Analytics
        </h3>
        <p data-id="element-694">
          Our website may use minimal analytics cookies to understand how
          visitors interact with our pages. These cookies do not collect
          personally identifiable information. You can disable cookies in your
          browser settings at any time without affecting your ability to access
          purchased content.
        </p>
      </div>

      <div data-id="element-695">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-696">
          4. Digital Product Delivery
        </h3>
        <p data-id="element-697">
          Upon successful payment, you will receive instant access to your
          digital products via download links sent to the email address provided
          at checkout. These links are for your personal use only and should not
          be shared or distributed.
        </p>
      </div>

      <div data-id="element-698">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-699">
          5. Third-Party Services
        </h3>
        <p data-id="element-700">
          We use trusted third-party services for payment processing and email
          delivery. These providers have their own privacy policies and handle
          your data in accordance with applicable data protection regulations.
        </p>
      </div>

      <div data-id="element-701">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-702">
          6. Your Rights
        </h3>
        <p data-id="element-703">
          You have the right to access, correct, or request deletion of your
          personal data at any time. You may also opt out of any marketing
          communications. To exercise these rights, please reach out through the
          channels provided in your purchase confirmation email.
        </p>
      </div>

      <div data-id="element-704">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-705">
          7. Data Security
        </h3>
        <p data-id="element-706">
          We implement industry-standard security measures to protect your
          personal information. All transactions are encrypted using SSL
          technology. However, no method of electronic transmission or storage
          is 100% secure, and we cannot guarantee absolute security.
        </p>
      </div>

      <div data-id="element-707">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-708">
          8. Changes to This Policy
        </h3>
        <p data-id="element-709">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date. Your continued
          use of our services after any changes constitutes acceptance of the
          updated policy.
        </p>
      </div>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-neutral-500 text-xs" data-id="element-710">Last updated: February 2026</p>

      <div data-id="element-711">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-712">
          1. Digital Product License
        </h3>
        <p data-id="element-713">
          Upon purchase, you are granted a non-exclusive, non-transferable,
          personal license to access and use the digital content (ebook and/or
          audiobook) for your own private, non-commercial use. This license does
          not transfer ownership of the intellectual property contained within
          the product.
        </p>
      </div>

      <div data-id="element-714">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-715">
          2. Delivery of Digital Products
        </h3>
        <p data-id="element-716">
          All products are delivered digitally. Upon successful payment, you
          will receive immediate access to download your purchased content. It
          is your responsibility to download and store the files. We recommend
          backing up your purchased files for future access.
        </p>
      </div>

      <div data-id="element-717">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-718">
          3. Refund Policy
        </h3>
        <p data-id="element-719">
          Due to the nature of digital products, all sales are final once the
          content has been accessed or downloaded. If you experience technical
          issues preventing you from accessing your purchase, please reach out
          through the support channels provided in your confirmation email and
          we will work to resolve the issue promptly.
        </p>
      </div>

      <div data-id="element-720">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-721">
          4. Intellectual Property
        </h3>
        <p data-id="element-722">
          All content, including but not limited to text, audio recordings,
          graphics, and design elements, is the intellectual property of Nicolás
          Pereira and Gabriel Beltrán. Unauthorized reproduction, distribution,
          modification, or resale of any content is strictly prohibited and may
          result in legal action.
        </p>
      </div>

      <div data-id="element-723">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-724">
          5. Permitted Use
        </h3>
        <p data-id="element-725">
          You may: read or listen to the content on any personal device you own,
          make a reasonable number of backup copies for personal use, and quote
          brief excerpts for non-commercial purposes with proper attribution.
          You may not: share download links or files with others, reproduce or
          distribute the content in any form, use the content for commercial
          purposes, or remove any copyright notices from the materials.
        </p>
      </div>

      <div data-id="element-726">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-727">
          6. Limitation of Liability
        </h3>
        <p data-id="element-728">
          The content provided is for informational and educational purposes
          only. The authors make no warranties or representations regarding the
          completeness, accuracy, or applicability of the information. In no
          event shall the authors be liable for any indirect, incidental, or
          consequential damages arising from the use of the content.
        </p>
      </div>

      <div data-id="element-729">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-730">
          7. Disclaimer
        </h3>
        <p data-id="element-731">
          The ideas, frameworks, and perspectives presented in this book are the
          authors' own and are intended to provoke thought and expand
          understanding. They are not intended as medical, psychological, or
          professional advice. Readers should exercise their own judgment and
          consult qualified professionals when appropriate.
        </p>
      </div>

      <div data-id="element-732">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-733">
          8. Modifications to Terms
        </h3>
        <p data-id="element-734">
          We reserve the right to modify these terms at any time. Updated terms
          will be posted on this page. Continued use of our products and
          services following any changes constitutes acceptance of the revised
          terms.
        </p>
      </div>

      <div data-id="element-735">
        <h3 className="font-serif text-base text-white font-semibold mb-2" data-id="element-736">
          9. Governing Law
        </h3>
        <p data-id="element-737">
          These terms shall be governed by and construed in accordance with
          applicable international digital commerce laws. Any disputes arising
          from these terms will be resolved through good-faith negotiation
          before pursuing any formal legal proceedings.
        </p>
      </div>
    </>;
}